import type {
  HoldingUnitLoadSessionGroupClaimStatus,
  HoldingUnitLoadSessionStatus,
} from '../holding-units/holding-units.dto.js';
import type { GetHoldingUnitLoadSessionDetailResponse } from '../holding-units/holding-units-queries.dto.js';
import type { ShipmentEvidenceSummary, ShipmentScaleTicketSummary } from './get-shipment.dto.js';
import type { ShipmentStatus } from './shipments-types.js';

export const shipmentWorkflowStateValues = [
  'draft',
  'active_pending_departure',
  'active_departed',
  'active_processor_follow_up',
  'active_completed',
  'voided',
] as const;
export type ShipmentWorkflowState = (typeof shipmentWorkflowStateValues)[number];

export interface ShipmentLifecycleInput {
  status: ShipmentStatus;
  departedAt?: string | null;
  receivedWeightLb?: number | null;
  scaleTicketSummary?: Pick<ShipmentScaleTicketSummary, 'processorTicketCount'> | null;
}

export interface ShipmentLifecycleFacts {
  persistedStatus: ShipmentStatus;
  workflowState: ShipmentWorkflowState;
  departureRecorded: boolean;
  processorTicketRecorded: boolean;
  processorReceiptRecorded: boolean;
}

export type ShipmentPresentationStage =
  | 'draft'
  | 'active'
  | 'departure_recorded'
  | 'processor_follow_up_pending'
  | 'completed'
  | 'voided';

export interface ShipmentPresentationChip {
  label: string;
  color: 'default' | 'primary' | 'success' | 'warning';
}

export interface ShipmentPresentationSummary {
  stage: ShipmentPresentationStage;
  currentLabel: string;
  nextAction: string;
  chips: ShipmentPresentationChip[];
}

export type ShipmentPresentationInput = ShipmentLifecycleInput;

const departureEligibleLoadSessionStatuses = new Set<HoldingUnitLoadSessionStatus>([
  'ready_to_depart',
]);

export function deriveShipmentLifecycleFacts(
  input: ShipmentLifecycleInput,
): ShipmentLifecycleFacts {
  const departureRecorded = Boolean(input.departedAt);
  const processorReceiptRecorded =
    input.receivedWeightLb !== null && input.receivedWeightLb !== undefined;
  const processorTicketRecorded = (input.scaleTicketSummary?.processorTicketCount ?? 0) > 0;

  if (input.status === 'VOIDED') {
    return {
      persistedStatus: input.status,
      workflowState: 'voided',
      departureRecorded,
      processorTicketRecorded,
      processorReceiptRecorded,
    };
  }

  if (input.status === 'DRAFT') {
    return {
      persistedStatus: input.status,
      workflowState: 'draft',
      departureRecorded: false,
      processorTicketRecorded: false,
      processorReceiptRecorded: false,
    };
  }

  if (!departureRecorded) {
    return {
      persistedStatus: input.status,
      workflowState: 'active_pending_departure',
      departureRecorded: false,
      processorTicketRecorded: false,
      processorReceiptRecorded: false,
    };
  }

  if (processorReceiptRecorded) {
    return {
      persistedStatus: input.status,
      workflowState: 'active_completed',
      departureRecorded: true,
      processorTicketRecorded,
      processorReceiptRecorded: true,
    };
  }

  if (processorTicketRecorded) {
    return {
      persistedStatus: input.status,
      workflowState: 'active_processor_follow_up',
      departureRecorded: true,
      processorTicketRecorded: true,
      processorReceiptRecorded: false,
    };
  }

  return {
    persistedStatus: input.status,
    workflowState: 'active_departed',
    departureRecorded: true,
    processorTicketRecorded: false,
    processorReceiptRecorded: false,
  };
}

export function buildShipmentPresentationSummary(
  input: ShipmentPresentationInput,
): ShipmentPresentationSummary {
  const stage = resolveShipmentPresentationStage(input);

  switch (stage) {
    case 'voided':
      return {
        stage,
        currentLabel: 'Voided',
        nextAction: 'Review only',
        chips: [{ label: 'Voided', color: 'default' }],
      };
    case 'draft':
      return {
        stage,
        currentLabel: 'Unfinished shipment',
        nextAction: 'Finish and save',
        chips: [{ label: 'Unfinished shipment', color: 'warning' }],
      };
    case 'active':
      return {
        stage,
        currentLabel: 'Saved shipment',
        nextAction: 'Check load readiness and record departure',
        chips: [{ label: 'Reserved', color: 'primary' }],
      };
    case 'departure_recorded':
      return {
        stage,
        currentLabel: 'Departure recorded',
        nextAction: 'Add processor ticket',
        chips: [{ label: 'Departure recorded', color: 'success' }],
      };
    case 'processor_follow_up_pending':
      return {
        stage,
        currentLabel: 'Processor follow-up pending',
        nextAction: 'Complete processor ticket',
        chips: [
          { label: 'Departure recorded', color: 'success' },
          { label: 'Processor follow-up pending', color: 'warning' },
        ],
      };
    case 'completed':
      return {
        stage,
        currentLabel: 'Completed',
        nextAction: 'Review reporting and export',
        chips: [
          { label: 'Departure recorded', color: 'success' },
          { label: 'Completed', color: 'success' },
        ],
      };
    default:
      return assertNever(stage);
  }
}

export function resolveShipmentPresentationStage(
  input: ShipmentPresentationInput,
): ShipmentPresentationStage {
  const facts = deriveShipmentLifecycleFacts(input);

  switch (facts.workflowState) {
    case 'draft':
      return 'draft';
    case 'active_pending_departure':
      return 'active';
    case 'active_departed':
      return 'departure_recorded';
    case 'active_processor_follow_up':
      return 'processor_follow_up_pending';
    case 'active_completed':
      return 'completed';
    case 'voided':
      return 'voided';
    default:
      return assertNever(facts.workflowState);
  }
}

export function buildShipmentEvidenceSummaryFromLoadSessionDetail(
  detail: GetHoldingUnitLoadSessionDetailResponse,
  groupId: string | null,
): ShipmentEvidenceSummary {
  const matchingGroup = groupId
    ? (detail.groups.find((candidate) => candidate.group.id === groupId) ?? null)
    : null;
  const discrepancyReview =
    matchingGroup && 'discrepancyReview' in matchingGroup ? matchingGroup.discrepancyReview : null;

  return {
    loadSessionId: detail.loadSession.id,
    loadSessionAvailable: true,
    loadSessionName: detail.loadSession.sessionName ?? null,
    loadSessionStatus: detail.loadSession.status,
    groupId,
    groupAvailable: matchingGroup !== null,
    groupMaterialId: matchingGroup?.group.materialId ?? null,
    claimStatus: matchingGroup?.claimStatus ?? null,
    unitCount: matchingGroup?.totals.unitCount ?? null,
    firstWeighTotal: matchingGroup?.totals.firstWeighTotal ?? null,
    reweighTotal: matchingGroup?.totals.reweighTotal ?? null,
    differenceTotal: matchingGroup?.totals.differenceTotal ?? null,
    officialForShipment: matchingGroup?.totals.officialForShipment ?? null,
    unresolvedDiscrepancyCount: discrepancyReview?.unresolvedCount ?? 0,
  };
}

export function getShipmentLoadSessionStatusLabel(
  status: HoldingUnitLoadSessionStatus | null,
): string | null {
  switch (status) {
    case 'open':
      return 'Still loading';
    case 'ready_to_depart':
      return 'Ready for departure';
    case 'canceled':
      return 'Canceled';
    default:
      return null;
  }
}

export function getShipmentEvidenceClaimStatusLabel(
  status: HoldingUnitLoadSessionGroupClaimStatus | null,
): string | null {
  switch (status) {
    case 'claimed':
      return 'Reserved for this shipment';
    case 'unclaimed':
      return 'Not reserved yet';
    case 'consumed':
      return 'Consumed by departed shipment';
    default:
      return null;
  }
}

export function resolveShipmentLoadSessionDepartureBlockReason(
  status: HoldingUnitLoadSessionStatus | null,
  unresolvedDiscrepancyCount = 0,
): string | null {
  if (unresolvedDiscrepancyCount > 0) {
    return unresolvedDiscrepancyCount === 1
      ? '1 measured discrepancy is still unresolved. Resolve it in Truck Loading before Mark Departed.'
      : `${String(unresolvedDiscrepancyCount)} measured discrepancies are still unresolved. Resolve them in Truck Loading before Mark Departed.`;
  }

  if (!status || departureEligibleLoadSessionStatuses.has(status)) {
    return null;
  }

  switch (status) {
    case 'open':
      return 'Truck load is still loading. Set ready for departure in Truck Loading before Mark Departed.';
    case 'canceled':
      return 'Attached truck load was canceled. Reattach valid evidence before Mark Departed.';
    default:
      return 'Truck load is not ready for departure yet.';
  }
}

function assertNever(value: never): never {
  throw new Error(`Unsupported shipment lifecycle state: ${String(value)}`);
}
